<template>
  <div class="electrical-visualization-container">
      <!-- Simplified Combined Header -->
      <div class="electrical-header q-pa-sm bg-grey-2">
        <div class="row items-center q-gutter-sm">
          <!-- Title -->
          <div class="text-subtitle1 text-weight-bold q-mr-md">Electrical Distribution</div>

          <q-separator vertical />

          <!-- Combined Stats/Legend - Clickable for unallocated -->
          <q-chip
            :clickable="unallocatedCounts.msb > 0"
            color="red"
            text-color="white"
            size="sm"
            :outline="unallocatedCounts.msb === 0"
            @click="unallocatedCounts.msb > 0 && emit('showUnallocatedList', 'MSB')"
          >
            MSB: {{ unallocatedCounts.msb }}
            <q-tooltip v-if="unallocatedCounts.msb > 0">Click to view unallocated</q-tooltip>
          </q-chip>

          <q-chip
            :clickable="unallocatedCounts.mssb > 0"
            color="orange"
            text-color="white"
            size="sm"
            :outline="unallocatedCounts.mssb === 0"
            @click="unallocatedCounts.mssb > 0 && emit('showUnallocatedList', 'MSSB')"
          >
            MSSB: {{ unallocatedCounts.mssb }}
            <q-tooltip v-if="unallocatedCounts.mssb > 0">Click to view unallocated</q-tooltip>
          </q-chip>

          <q-chip
            :clickable="unallocatedCounts.db > 0"
            color="blue"
            text-color="white"
            size="sm"
            :outline="unallocatedCounts.db === 0"
            @click="unallocatedCounts.db > 0 && emit('showUnallocatedList', 'DB')"
          >
            DB: {{ unallocatedCounts.db }}
            <q-tooltip v-if="unallocatedCounts.db > 0">Click to view unallocated</q-tooltip>
          </q-chip>

          <q-chip
            :clickable="unallocatedCounts.ddc > 0"
            color="yellow"
            text-color="black"
            size="sm"
            :outline="unallocatedCounts.ddc === 0"
            @click="unallocatedCounts.ddc > 0 && emit('showUnallocatedList', 'DDC')"
          >
            DDC: {{ unallocatedCounts.ddc }}
            <q-tooltip v-if="unallocatedCounts.ddc > 0">Click to view unallocated</q-tooltip>
          </q-chip>

          <q-space />

          <!-- Search Box (smaller) -->
          <q-input
            :model-value="electricalSearchQuery"
            placeholder="Search..."
            dense
            outlined
            clearable
            style="max-width: 150px"
            @update:model-value="emit('update:electricalSearchQuery', $event)"
          >
            <template #prepend>
              <q-icon name="search" size="xs" />
            </template>
          </q-input>

          <!-- Simplified Expand/Collapse -->
          <q-btn-group flat dense>
            <q-btn
              flat
              size="sm"
              icon="unfold_less"
              dense
              @click="emit('expandElectricalToLevel', 2)"
            >
              <q-tooltip>Collapse All</q-tooltip>
            </q-btn>
            <q-btn
              flat
              size="sm"
              icon="unfold_more"
              dense
              @click="emit('expandElectricalToLevel', 999)"
            >
              <q-tooltip>Expand All</q-tooltip>
            </q-btn>
          </q-btn-group>

          <!-- Single Refresh Button -->
          <q-btn
            flat
            round
            size="sm"
            color="primary"
            icon="refresh"
            :loading="loadingElectrical"
            dense
            @click="emit('loadElectricalHierarchy')"
          >
            <q-tooltip>Refresh</q-tooltip>
          </q-btn>
        </div>
      </div>

      <!-- Split View: Tree + Details Sidebar -->
      <div class="chart-containers-wrapper row no-wrap">
        <!-- Electrical Hierarchy Tree Container (Left Side) -->
        <div :class="selectedElectricalNode ? 'col-8' : 'col'" class="chart-container q-pa-md">
          <q-tree
            v-if="electricalTreeNodes && electricalTreeNodes.length > 0"
            :nodes="electricalTreeNodes"
            node-key="id"
            :expanded="electricalExpandedNodes"
            no-connectors
            :selected="selectedElectricalNode"
            @update:expanded="emit('update:electricalExpandedNodes', $event)"
            @update:selected="emit('update:selectedElectricalNode', $event)"
          >
            <template #default-header="prop">
              <div
                class="row items-center full-width q-hoverable"
                @contextmenu.prevent="showElectricalContextMenu({ event: $event, node: prop.node })"
              >
                <q-icon :name="prop.node.icon" class="q-mr-sm" :color="prop.node.color" />
                <div class="col">
                  <div class="row items-center">
                    <div class="text-weight-bold">{{ prop.node.label }}</div>
                    <q-badge
                      v-if="prop.node.meter_count > 0"
                      color="green"
                      class="q-ml-sm"
                    >
                      <q-icon name="speed" size="xs" class="q-mr-xs" />
                      {{ prop.node.meter_count }}
                    </q-badge>
                    <q-badge
                      v-if="prop.node.db_count > 0"
                      color="blue"
                      class="q-ml-sm"
                    >
                      DB: {{ prop.node.db_count }}
                    </q-badge>
                  </div>
                  <div class="text-caption text-grey-7">
                    {{ prop.node.brick_class || prop.node.type }}
                  </div>
                </div>
                <div class="q-gutter-xs">
                  <!-- Add Existing Switchboard button based on node type -->
                  <q-btn
                    v-if="canAddSwitchboard(prop.node)"
                    size="sm"
                    round
                    color="primary"
                    icon="add"
                    @click.stop="emit('openAddExistingSwitchboardDialog', prop.node)"
                  >
                    <q-tooltip>Add Existing Switchboard</q-tooltip>
                  </q-btn>

                  <!-- Delete button -->
                  <q-btn
                    v-if="prop.node.type !== 'transformer'"
                    size="sm"
                    round
                    color="negative"
                    icon="delete"
                    @click.stop="emit('deleteElectricalNode', prop.node)"
                  >
                    <q-tooltip>Delete {{ prop.node.label }}</q-tooltip>
                  </q-btn>

                </div>
              </div>
            </template>
          </q-tree>
          <div v-else-if="!loadingElectrical" class="text-center q-pa-xl">
            <q-icon name="electrical_services" size="64px" color="grey-5" />
            <div class="text-h6 text-grey-7 q-mt-md">No electrical equipment found</div>
            <div class="text-body2 text-grey-6">Add electrical equipment to see the hierarchy</div>
          </div>
        </div>

        <!-- Details Sidebar (Right Side) - Only show when equipment is selected -->
        <details-side-panel
          :model-value="!!selectedElectricalNode"
          width="400px"
          max-width="500px"
          min-width="350px"
        >
          <electrical-details-sidebar
            :selected-equipment="selectedEquipmentData"
            :hierarchy-data="electricalData"
            @refresh="emit('loadElectricalHierarchy')"
            @select-equipment="handleEquipmentSelect"
            @view-controller-points="handleViewControllerPoints"
          />
        </details-side-panel>
      </div>

      <!-- Loading Overlay -->
      <q-inner-loading :showing="loadingElectrical" style="background-color: rgba(255, 255, 255, 0.9);">
        <q-spinner size="50px" color="primary" />
        <div class="text-primary q-mt-md">Loading electrical data...</div>
      </q-inner-loading>

      <!-- Context Menu -->
      <q-menu
        v-model="showContextMenu"
        :target="contextMenuTarget"
        context-menu
      >
        <q-list dense style="min-width: 200px">
          <template v-for="(item, index) in contextMenuItems" :key="index">
            <q-separator v-if="item.separator" />
            <q-item
              v-else
              v-close-popup
              clickable
              @click="item.click"
            >
              <q-item-section avatar>
                <q-icon :name="item.icon" :color="item.color" />
              </q-item-section>
              <q-item-section>{{ item.label }}</q-item-section>
            </q-item>
          </template>
        </q-list>
      </q-menu>

      <!-- No Data Message -->
      <div v-if="!loadingElectrical && (!electricalData || electricalData.length === 0)" class="text-center q-pa-xl text-grey">
        <q-icon name="electrical_services" size="3em" class="q-mb-md" />
        <div class="text-h6">No Electrical Infrastructure Data</div>
        <div class="text-body2">Add existing switchboards from the hierarchy to visualize electrical relationships</div>
        <q-btn
          color="primary"
          icon="add"
          label="Add Existing Switchboard"
          class="q-mt-md"
          @click="emit('openAddExistingSwitchboardDialog', null)"
        />
      </div>
  </div>
</template>

<script setup>
  import { defineProps, defineEmits, computed, ref } from 'vue';
  import ElectricalDetailsSidebar from '../ElectricalDetailsSidebar.vue';
  import DetailsSidePanel from '@/components/common/DetailsSidePanel.vue';

  const props = defineProps({
    unallocatedCounts: {
      type: Object,
      required: true,
    },
    electricalSearchQuery: {
      type: String,
      default: '',
    },
    loadingElectrical: {
      type: Boolean,
      default: false,
    },
    electricalTreeNodes: {
      type: Array,
      required: true,
    },
    electricalExpandedNodes: {
      type: Array,
      required: true,
    },
    selectedElectricalNode: {
      type: String,
      default: '',
    },
    electricalData: {
      type: Array,
      default: () => [],
    },
    canAddSwitchboard: {
      type: Function,
      required: true,
    },
  });

  const emit = defineEmits([
    'update:electricalSearchQuery',
    'update:electricalExpandedNodes',
    'update:selectedElectricalNode',
    'showUnallocatedList',
    'expandElectricalToLevel',
    'loadElectricalHierarchy',
    'openAddExistingSwitchboardDialog',
    'editElectricalNode',
    'deleteElectricalNode',
    'viewControllerIo',
    'openDdcAllocationDialog',
    'deallocateDdcController',
    'openPowerConnectionDialog',
    'disconnectPower',
    'openSwitchboardClassificationDialog',
  ]);

  // Find selected equipment from tree
  const selectedEquipmentData = computed(() => {
    if (!props.selectedElectricalNode || !props.electricalTreeNodes) {return null;}

    // Recursively search tree for selected node
    const findNode = (nodes, id) => {
      for (const node of nodes) {
        // Use loose equality to handle string/number mismatch
        if (node.id == id) {
          return node;
        }
        if (node.children) {
          const found = findNode(node.children, id);
          if (found) {return found;}
        }
      }
      return null;
    };

    return findNode(props.electricalTreeNodes, props.selectedElectricalNode);
  });

  // Handle equipment selection from sidebar
  const handleEquipmentSelect = (equipment) => {
    emit('update:selectedElectricalNode', equipment.id);
  };

  // Handle viewing controller points - open the I/O usage dialog
  const handleViewControllerPoints = (controller) => {
    // Emit event to open the Controller I/O Usage dialog
    emit('viewControllerIo', controller);
  };

  // Context menu state
  const showContextMenu = ref(false);
  const contextMenuTarget = ref(null);
  const contextMenuItems = ref([]);

  // Context menu handling
  const showElectricalContextMenu = (payload) => {
    const { event, node } = payload;

    if (!node) {return;}

    const brickClass = node.brick_class || '';
    const equipmentData = node.data || {};
    const menuItems = [];

    contextMenuTarget.value = event.currentTarget;

    // Add Existing Switchboard option (if applicable)
    if (props.canAddSwitchboard(node)) {
      menuItems.push({
        label: 'Add Existing Switchboard',
        icon: 'add',
        click: () => emit('openAddExistingSwitchboardDialog', node),
      });
    }

    // DDC Controller Allocation (for equipment that isn't already a DDC panel)
    if (node.type === 'equipment' && !brickClass.includes('DDC_Panel')) {
      if (equipmentData.controller) {
        menuItems.push({
          label: 'Change DDC Controller',
          icon: 'swap_horiz',
          click: () => emit('openDdcAllocationDialog', node),
        });
        menuItems.push({
          label: 'Deallocate Controller',
          icon: 'link_off',
          click: () => emit('deallocateDdcController', node),
        });
      } else {
        menuItems.push({
          label: 'Allocate DDC Controller',
          icon: 'memory',
          click: () => emit('openDdcAllocationDialog', node),
        });
      }
    }

    // Power Connection (for equipment)
    if (node.type === 'equipment') {
      if (equipmentData.power_source) {
        menuItems.push({
          label: 'Change Power Source',
          icon: 'power',
          click: () => emit('openPowerConnectionDialog', node),
        });
        menuItems.push({
          label: 'Disconnect Power',
          icon: 'power_off',
          click: () => emit('disconnectPower', node),
        });
      } else {
        menuItems.push({
          label: 'Connect Power Source',
          icon: 'electrical_services',
          click: () => emit('openPowerConnectionDialog', node),
        });
      }
    }

    // Switchboard Classification (for MSSB only)
    if (brickClass.includes('Mechanical_Services_Switchboard')) {
      menuItems.push({
        label: 'Set Classification',
        icon: 'label',
        click: () => emit('openSwitchboardClassificationDialog', node),
      });
    }

    // Add separator if we have items
    if (menuItems.length > 0) {
      menuItems.push({ separator: true });
    }

    // Delete option (not for transformers)
    if (node.type !== 'transformer') {
      menuItems.push({
        label: `Delete ${node.label}`,
        icon: 'delete',
        color: 'negative',
        click: () => emit('deleteElectricalNode', node),
      });
    }

    // Set menu items and show context menu
    contextMenuItems.value = menuItems;
    showContextMenu.value = true;
  };
</script>

<style scoped>
.electrical-visualization-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.electrical-header {
  flex-shrink: 0;
}

.chart-containers-wrapper {
  flex: 1;
  overflow: auto;
}

.chart-container {
  height: 100%;
  transition: all 0.3s ease;
}
</style>
